import { useState } from 'react';

// Custom Slab SVG component mimicking a graded card holder
const SlabIcon = ({ filled, color, size = 'md', onClick, interactive }) => {
  const sizeMap = {
    sm: { width: 16, height: 20 },
    md: { width: 20, height: 25 },
    lg: { width: 28, height: 35 }
  };
  
  const { width, height } = sizeMap[size] || sizeMap.md;
  
  // Color scheme based on rating
  const getColors = () => {
    if (!filled) {
      return {
        outer: '#3f3f46', // zinc-700
        inner: '#27272a', // zinc-800
        label: '#52525b'  // zinc-600
      };
    }
    
    switch (color) {
      case 'gold':
        return {
          outer: '#f59e0b', // amber-500
          inner: '#fbbf24', // amber-400
          label: '#d97706'  // amber-600
        };
      case 'green':
        return {
          outer: '#10b981', // emerald-500 (Society Green)
          inner: '#34d399', // emerald-400
          label: '#059669'  // emerald-600
        };
      case 'gray':
      default:
        return {
          outer: '#a1a1aa', // zinc-400
          inner: '#d4d4d8', // zinc-300
          label: '#71717a'  // zinc-500
        };
    }
  };
  
  const colors = getColors();
  
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      onClick={onClick}
      className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}
      style={{ filter: filled && color === 'gold' ? 'drop-shadow(0 0 3px rgba(245, 158, 11, 0.5))' : 'none' }}
    >
      {/* Outer case */}
      <rect
        x="1"
        y="1"
        width="18"
        height="23"
        rx="2"
        fill={colors.outer}
        stroke={filled ? colors.outer : '#52525b'}
        strokeWidth="1"
      />
      
      {/* Inner card window */}
      <rect
        x="3"
        y="5"
        width="14"
        height="16"
        rx="1"
        fill={colors.inner}
        opacity={filled ? 1 : 0.5}
      />
      
      {/* Label area at top */}
      <rect
        x="4"
        y="2"
        width="12"
        height="2"
        rx="0.5"
        fill={colors.label}
      />
      
      {/* Card silhouette inside */}
      <rect
        x="5"
        y="7"
        width="10"
        height="12"
        rx="0.5"
        fill={filled ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'}
      />
    </svg>
  );
};

// Determine slab color based on rating
const getSlabColor = (rating, index, currentRating) => {
  const isActive = index <= currentRating;
  
  if (!isActive) return null;
  
  if (rating >= 4.5) return 'gold';
  if (rating > 0) return 'green';
  return 'gray';
};

export const SlabRating = ({ 
  rating = 0, 
  onRate, 
  readonly = false, 
  size = 'md',
  showValue = false 
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  const slabs = [1, 2, 3, 4, 5];
  const displayRating = hoverRating || rating;
  
  // Determine the color based on actual rating
  const slabColor = rating >= 4.5 ? 'gold' : rating > 0 ? 'green' : 'gray';
  
  return (
    <div className="flex items-center gap-1" data-testid="slab-rating">
      <div className="flex items-center gap-0.5">
        {slabs.map((slab) => (
          <div
            key={slab}
            onMouseEnter={() => !readonly && setHoverRating(slab)}
            onMouseLeave={() => !readonly && setHoverRating(0)}
            onClick={() => !readonly && onRate && onRate(slab)}
          >
            <SlabIcon
              filled={slab <= displayRating}
              color={slab <= displayRating ? slabColor : null}
              size={size}
              interactive={!readonly}
            />
          </div>
        ))}
      </div>
      {showValue && (
        <span className={`ml-1 font-medium ${
          rating >= 4.5 ? 'text-amber-500' : rating > 0 ? 'text-emerald-500' : 'text-zinc-500'
        }`}>
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

// Single slab icon for display purposes
export const SingleSlab = ({ rating, size = 'md' }) => {
  const color = rating >= 4.5 ? 'gold' : rating > 0 ? 'green' : 'gray';
  return <SlabIcon filled={rating > 0} color={color} size={size} />;
};

export default SlabRating;
